import { useState, useEffect, useRef, useCallback } from "react";
import { Spinner, ProgressBar, OPTS } from "../../shared/ui";

const PIXEL_STEPS   = [8, 16, 32, 64, 0]; // 0 = оригинал
const STEP_INTERVAL = 4000;

function PixelCanvas({ src, step, size, onError }) {
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img || !img.complete || !img.naturalWidth) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width, h = canvas.height;
    if (step === 0) {
      ctx.clearRect(0, 0, w, h);
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(img, 0, 0, w, h);
      return;
    }
    const buf = document.createElement("canvas");
    buf.width = step; buf.height = step;
    buf.getContext("2d").drawImage(img, 0, 0, step, step);
    ctx.clearRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(buf, 0, 0, w, h);
  }, [step, src]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <>
      <img ref={imgRef} src={src} alt="" crossOrigin="anonymous" onLoad={draw} onError={onError} style={{ display: "none" }} />
      <canvas ref={canvasRef} width={size} height={size} style={{ width: size, height: size, imageRendering: "pixelated" }} />
    </>
  );
}

export default function CarsHost({ question, logoSlug, options, answered, players, result, tv }) {
  const [imgOk,   setImgOk]   = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const timerRef = useRef(null);
  const size = tv ? 180 : 130;

  useEffect(() => {
    setImgOk(true);  // явный сброс при каждой смене логотипа
    setStepIdx(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setStepIdx(prev => {
        if (prev >= PIXEL_STEPS.length - 1) { clearInterval(timerRef.current); return prev; }
        return prev + 1;
      });
    }, STEP_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [logoSlug]);

  useEffect(() => {
    if (result) { clearInterval(timerRef.current); setStepIdx(PIXEL_STEPS.length - 1); }
  }, [result]);

  if (!question && !logoSlug) return <Spinner color="#FF6B00" tv={tv} />;

  const currentStep = PIXEL_STEPS[stepIdx];
  const isRevealed  = currentStep === 0;
  const stepsLeft   = PIXEL_STEPS.length - 1 - stepIdx;

  return (
    <div className="w-full animate-slide-up">
      <div className={`flex ${tv ? "flex-row gap-8 items-start" : "flex-col gap-4"} w-full`}>

        <div className={`${tv ? "w-[40%]" : "w-full"} flex flex-col items-center gap-3`}>
          <div style={{
            background: "#fff", border: `4px solid ${result && isRevealed ? "#3DFF3D" : "#000"}`,
            borderRadius: "1.5rem", boxShadow: "6px 6px 0 #000",
            padding: tv ? "2.5rem" : "1.5rem",
            display: "flex", alignItems: "center", justifyContent: "center",
            width: "100%", minHeight: tv ? "220px" : "160px",
            transition: "border-color 0.3s", position: "relative",
          }}>
            {imgOk && logoSlug
              ? <PixelCanvas src={`/api/cars/logo/${encodeURIComponent(logoSlug)}`} step={currentStep} size={size} onError={() => setImgOk(false)} />
              : <div style={{ fontSize: tv ? "5rem" : "3.5rem", color: "#333" }}>🚗</div>
            }
            {!result && (
              <div style={{ position: "absolute", bottom: "0.5rem", right: "0.75rem", display: "flex", gap: "4px" }}>
                {PIXEL_STEPS.map((_, i) => (
                  <div key={i} style={{
                    width: "8px", height: "8px", borderRadius: "50%",
                    background: i <= stepIdx ? "#FF6B00" : "#33333388",
                    transition: "background 0.3s",
                  }} />
                ))}
              </div>
            )}
          </div>

          <div style={{
            background: "#0a0a14", border: "2.5px solid #FF6B0044", borderRadius: "0.75rem",
            padding: tv ? "0.5rem 1.2rem" : "0.3rem 0.9rem",
            fontFamily: "'Nunito', sans-serif", fontSize: tv ? "0.9rem" : "0.7rem",
            color: "#FF6B00", textAlign: "center",
          }}>
            {isRevealed
              ? "🏎️ Логотип открыт!"
              : `🔍 Чёткость повысится ещё ${stepsLeft} ${stepsLeft === 1 ? "раз" : "раза"}`}
          </div>

          {!result && (
            <div style={{ width: "100%" }}>
              <div className="font-heading text-[#aaa] mb-2" style={{ fontSize: tv ? "1.1rem" : "0.8rem", textAlign: "center" }}>
                Ответили: {answered.length}/{players.length}
              </div>
              <ProgressBar value={answered.length} max={players.length} color="#FF6B00" tv={tv} />
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {players.map((p, i) => (
                  <div key={i} className="player-chip" style={{
                    backgroundColor: p.color, opacity: answered.includes(p.name) ? 1 : 0.3,
                    fontSize: tv ? "0.95rem" : "0.7rem",
                    padding: tv ? "0.3rem 0.85rem" : "0.2rem 0.55rem", transition: "opacity 0.3s",
                  }}>{p.name}</div>
                ))}
              </div>
            </div>
          )}

          {result && (
            <div className="animate-bounce-in text-center" style={{
              background: "#0a1a0a", border: "2.5px solid #3DFF3D",
              borderRadius: "1rem", padding: tv ? "0.75rem 1.5rem" : "0.5rem 1rem", width: "100%",
            }}>
              <div className="font-heading" style={{ color: "#3DFF3D", fontSize: tv ? "1.4rem" : "1rem" }}>
                ✅ {options[result.correct]}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 grid grid-cols-2 gap-3" style={{ alignContent: "start" }}>
          {options.map((opt, i) => {
            const isCorrect = result && result.correct === i;
            const isWrong   = result && result.correct !== i;
            const whoVoted  = result ? Object.entries(result.answers || {}).filter(([,a]) => a === i).map(([n]) => n) : [];
            return (
              <div key={i} style={{
                background: OPTS.colors[i], border: "3px solid #000", borderRadius: "1.25rem",
                boxShadow: isCorrect ? "8px 8px 0 #000" : "5px 5px 0 #000",
                padding: tv ? "1.5rem 1rem" : "1.1rem 0.75rem", textAlign: "center",
                opacity: isWrong ? 0.4 : 1, transform: isCorrect ? "scale(1.04)" : "scale(1)",
                transition: "all 0.3s", position: "relative",
              }}>
                <div className="font-display" style={{ fontSize: tv ? "3rem" : "1.75rem", color: "#000", marginBottom: "0.3rem" }}>
                  {OPTS.labels[i]}
                </div>
                <div className="font-heading font-bold" style={{ fontSize: tv ? "1.3rem" : "0.85rem", color: "#000", lineHeight: 1.2 }}>
                  {opt}
                </div>
                {result && whoVoted.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1 mt-2">
                    {whoVoted.map(n => {
                      const pl = players.find(p => p.name === n);
                      return (
                        <span key={n} style={{
                          background: pl?.color || "#fff", border: "1.5px solid #000",
                          borderRadius: "99px", padding: "0.1rem 0.5rem",
                          fontSize: tv ? "0.8rem" : "0.6rem",
                          fontFamily: "'Fredoka One', sans-serif", color: "#000",
                        }}>{n}</span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}